import React, { useCallback, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import { useAnalyticsPageView, useEffectOnce, usePathParam } from '@chilecompra/react-kit'
import {
  Button,
  Card,
  Popover,
  PopoverMenuItem,
  Skeleton,
  Toast,
  Tooltip,
  Typography
} from '@chilecompra/react-kit/components'

import { REACT_APP_SANCTION_LEARN_SCORE_URL } from '../../config/settings/environment'
import { CONTRACTUAL_BEHAVIOR_TABS, SCORE_RANGE_COLORS } from '../../config/settings/constant'
import { PATH_PARAM } from '../../config/settings/regexes'
import routes from '../../config/settings/routes'
import { generateFormatedDate } from '../../modules/utils/formatters'
import { getLastSunday } from '../../modules/utils/helpers'
import { useScore } from '../../components/Score/Score.hooks'
import { useAuthorization } from '../AuthorizationProvider/AuthorizationProvider.hooks'
import {
  onGetAllSanctionsThunk,
  onGetContractualBehaviorSummaryByReviewsThunk,
  onGetContractualBehaviorSummaryThunk
} from './ContractualBehavior.actions'
import { onSetInitialState } from '../Sanction/Sanction.actions'
import SantionsTypeTable from './SanctionsTypeTable.container'
import ProtectByProfile from '../../components/Security/ProtectByProfile'
import Score from '../../components/Score/Score'
import { ReviewedContractsTable } from '../ReviewedContractsTable/ReviewedContractsTable.container'
import {
  Anchor,
  AnchorIcon,
  AnchorText,
  BaseGrid,
  BoxDivider,
  Content,
  ContentWrapper,
  ExternalLink,
  HelpIcon,
  LeftPanel,
  Separator,
  Strong,
  SubHeadingWrapper,
  Toaster
} from './ContractualBehavior.styles'

/**
 * The contractual behavior container.
 */
const ContractualBehavior = () => {
  const { userType } = useAuthorization()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const dni = usePathParam('rut')
  const [currentTab, setCurrentTab] = useState('Comportamiento base')
  const [tabsOpen, setTabsOpen] = useState(false)

  const {
    error,
    errorByReviews,
    isSystemScore,
    loading,
    loadingByReviews,
    max,
    months,
    nonComplianceSanctions,
    voidedSanctions,
    rate,
    rateByReviews,
    receivedContracts,
    reviewedContracts,
    requestedByReviews,
    purchaseOrders
  } = useSelector(state => state.contractualBehavior)

  useEffectOnce(() => {
    if (!loading || userType === '') return
    dispatch(onGetContractualBehaviorSummaryThunk({ dni }))
    dispatch(onGetAllSanctionsThunk({ dni }))
  }, [])

  useEffectOnce(() => {
    if (!loadingByReviews || requestedByReviews || userType === '') return
    dispatch(onGetContractualBehaviorSummaryByReviewsThunk({ dni }))
  }, [])

  const { color, updatedRate } = useScore({
    rangeValues: SCORE_RANGE_COLORS,
    rate,
    max
  })

  const { color: colorByReviews, updatedRate: updatedRateByReviews } = useScore({
    rangeValues: SCORE_RANGE_COLORS,
    rate: rateByReviews,
    max
  })

  const handleOnClick = () => {
    dispatch(onSetInitialState())
    navigate(routes.registerSanction.replace(PATH_PARAM, dni))
  }

  const handleTabChange = useCallback(
    tabName => () => {
      setTabsOpen(false)
      setCurrentTab(tabName)
    },
    []
  )

  const handleGoToHistoricalSanctionsList = () => {
    navigate(routes.historicalSanctionsList.replace(PATH_PARAM, dni), {
      replace: true
    })
  }

  const handleCloseTabs = useCallback(() => setTabsOpen(false), [])
  const handleOpenTabs = useCallback(() => setTabsOpen(true), [])
  useAnalyticsPageView('/ficha/comportamiento')
  return (error && error?.reason !== 'SOMETHING_WENT_WRONG_ERROR') ||
    (errorByReviews && errorByReviews.reason !== 'SOMETHING_WENT_WRONG_ERROR') ? null : (
    <Content>
      <Typography fontWeight="bold" padding="0 0 16px" variant="h4">
        Comportamiento del proveedor
      </Typography>
      <Card color="blank" enableBorder margin="0 0 32px" padding="21px 24px 8px">
        <BaseGrid container>
          <BoxDivider item xs={6}>
            <Typography fontWeight="bold" variant="body1">
              Nota comportamiento base
            </Typography>
            <Typography variant="subtitle">
              <ExternalLink href={REACT_APP_SANCTION_LEARN_SCORE_URL} padding="6px 0 10px" target="_blank">
                ¿Cómo se calcula esta nota?
              </ExternalLink>
            </Typography>
            <BaseGrid container alignItems="center">
              {(updatedRate === '0,00' || rate === -1) && (
                <Typography align="right" color="black3" margin="0 0 10px" fontWeight="bold" variant="h2">
                  Sin información
                </Typography>
              )}
              {updatedRate !== '0,00' && rate !== -1 && (
                <Score
                  align="flex-start"
                  color={isSystemScore ? 'black3' : color}
                  loading={loading}
                  max={max}
                  rate={updatedRate}
                />
              )}
            </BaseGrid>
            <ContentWrapper alignSelf="center" flexDirection="column" padding="10px 28px 26px 0">
              <ContentWrapper>
                <Typography fontSize="13px" fontWeight="bold" variant="body2" margin="0 10px 0 0">
                  {loading ? <Skeleton padding="0 8px" variant="rect" /> : purchaseOrders}
                </Typography>
                <Typography fontSize="13px" variant="body2">
                  Órdenes de compra aceptadas
                </Typography>
              </ContentWrapper>

              <ContentWrapper>
                <Typography fontSize="13px" fontWeight="bold" variant="body2" margin="0 10px 0 0">
                  {loading ? <Skeleton padding="0 8px" variant="rect" /> : nonComplianceSanctions}
                </Typography>
                <Typography fontSize="13px" variant="body2">
                  Sanciones por incumplimiento recibidas
                </Typography>
              </ContentWrapper>

              {!loading && String(voidedSanctions) !== '0' && (
                <ContentWrapper alignItems="center">
                  <Typography fontSize="13px" fontWeight="bold" variant="body2" margin="0 10px 0 0">
                    {voidedSanctions}
                  </Typography>
                  <Typography fontSize="13px" variant="body2">
                    Sanciones anuladas
                  </Typography>
                  <Tooltip
                    content={
                      <Typography color="white" variant="subtitle">
                        Sanciones dejadas sin efecto mediante un recurso de reposición aprobado.
                      </Typography>
                    }
                  >
                    <HelpIcon />
                  </Tooltip>
                </ContentWrapper>
              )}
            </ContentWrapper>
          </BoxDivider>
          <LeftPanel item xs={6} padding="0 0 0 21px">
            <BaseGrid container margin="0 0 0 24px">
              <Typography fontWeight="bold" variant="body1">
                Nota comportamiento por evaluación de contratos
              </Typography>
              <Typography variant="subtitle">
                <ExternalLink href={REACT_APP_SANCTION_LEARN_SCORE_URL} padding="6px 0 10px" target="_blank">
                  ¿Cómo se calcula esta nota?
                </ExternalLink>
              </Typography>
              <BaseGrid container alignItems="center">
                {!loadingByReviews && receivedContracts === 0 && !errorByReviews ? (
                  <>
                    <Typography align="right" color="black3" margin="0 0 10px" fontWeight="bold" variant="h2">
                      Sin información
                    </Typography>
                    <Typography align="left" variant="subtitle">
                      {`Este proveedor no ha recibido evaluaciones en ${
                        months === 1 ? 'el último mes' : `los últimos ${months} meses`
                      }.`}
                    </Typography>
                  </>
                ) : updatedRateByReviews === '0,00' || updatedRateByReviews === -1 ? (
                  <Typography align="right" color="black3" margin="0 0 10px" fontWeight="bold" variant="h2">
                    Sin información
                  </Typography>
                ) : (
                  <Score
                    align="flex-start"
                    color={isSystemScore ? 'black3' : colorByReviews}
                    loading={loadingByReviews}
                    max={max}
                    rate={updatedRateByReviews}
                  />
                )}
              </BaseGrid>
              {!loadingByReviews && receivedContracts === 0 && !errorByReviews ? null : (
                <ContentWrapper alignSelf="center" flexDirection="column" padding="10px 28px 26px 0">
                  <ContentWrapper>
                    <Typography fontSize="13px" fontWeight="bold" variant="body2" margin="0 10px 0 0">
                      {loadingByReviews ? <Skeleton padding="0 8px" variant="rect" /> : receivedContracts}
                    </Typography>
                    <Typography fontSize="13px" variant="body2">
                      Contratos recibidos
                    </Typography>
                  </ContentWrapper>

                  <ContentWrapper>
                    <Typography fontSize="13px" fontWeight="bold" variant="body2" margin="0 10px 0 0">
                      {loadingByReviews ? <Skeleton padding="0 8px" variant="rect" /> : reviewedContracts}
                    </Typography>
                    <Typography fontSize="13px" variant="body2">
                      Contratos evaluados
                    </Typography>
                  </ContentWrapper>
                </ContentWrapper>
              )}
            </BaseGrid>
          </LeftPanel>
        </BaseGrid>
        <Separator />
        <Typography variant="subtitle" gutterBottom>
          Se actualiza la información semanalmente. Última actualización el {generateFormatedDate(getLastSunday())},
          correspondiente a los últimos 24 meses de actividad en Mercado Público.
        </Typography>
      </Card>

      <Typography display="inline" variant="body1" margin="0 10px 0 0">
        Revisar detalle de:
      </Typography>
      <Popover
        anchor={
          <Anchor>
            <AnchorText>
              <Strong>{currentTab}</Strong>
            </AnchorText>
            <AnchorIcon open={tabsOpen} />
          </Anchor>
        }
        open={tabsOpen}
        onClose={handleCloseTabs}
        onOpen={handleOpenTabs}
      >
        {CONTRACTUAL_BEHAVIOR_TABS.map(tabName => (
          <PopoverMenuItem enableClose key={tabName} onClick={handleTabChange(tabName)}>
            {tabName}
          </PopoverMenuItem>
        ))}
      </Popover>
      <Card color="blank" enableBorder margin="24px 0" padding="32px 24px">
        {currentTab === 'Comportamiento base' ? (
          <>
            <BaseGrid container wrap="nowrap" justifyContent="space-between">
              <BaseGrid item>
                <Typography fontWeight="bold" padding="0 0 16px" variant="h4">
                  Comportamiento base
                </Typography>
                {!error && (
                  <SubHeadingWrapper>
                    <Typography variant="body2">
                      {`Estas son las sanciones aplicadas a la empresa proveedora por incumplimientos en Mercado Público, en ${
                        months === 1 ? 'el último mes' : `los últimos ${months} meses`
                      }.`}
                    </Typography>
                  </SubHeadingWrapper>
                )}
              </BaseGrid>
              <BaseGrid item>
                <ProtectByProfile rule={userType => userType === 'Comprador'}>
                  <Button color="error" variant="outlined" width="173px" padding="0" onClick={handleOnClick}>
                    Ingresar
                  </Button>
                </ProtectByProfile>
              </BaseGrid>
            </BaseGrid>

            {!error && (
              <Typography color="black3" italic margin="8px 0 0" variant="subtitle">
                La nota de comportamiento base y el número de sanciones por incumplimiento recibidas se actualizarán
                semanalmente, cada domingo, y quedarán visibles a partir de ese día.
              </Typography>
            )}

            <ProtectByProfile rule={userType => userType === 'Comprador'}>
              <BaseGrid container margin="10px 0 0">
                <BaseGrid item xs={12}>
                  <Toast severity="info" title="Dejar sin efecto sanciones" align="left">
                    <Typography variant="body2">
                      Para dejar sin efecto una sanción, ingresa a Ver detalle de la sanción del proveedor. Desde ahí
                      podrás gestionar según corresponda.
                    </Typography>
                  </Toast>
                </BaseGrid>
              </BaseGrid>
            </ProtectByProfile>

            {!error && <SantionsTypeTable type="tenders" />}

            {!error && <SantionsTypeTable type="frameworkAgreement" />}

            {!error && <SantionsTypeTable type="agilePurchase" />}

            {!error && <SantionsTypeTable type="directDeals" />}

            {!error && !loading && (
              <Button
                color="primary"
                margin="32px 0 0"
                onClick={handleGoToHistoricalSanctionsList}
                size="small"
                variant="outlined"
              >
                Ver sanciones históricas
              </Button>
            )}

            {!loading && error && (
              <Toaster
                margin="20px 0"
                border
                severity="error"
                textDirection="vertical"
                title="Lo sentimos, estamos presentando problemas con nuestro servidor"
              >
                En estos momentos tenemos problemas para disponibilizar la información. Por favor, consulta en unos
                minutos más.
              </Toaster>
            )}
          </>
        ) : (
          <ReviewedContractsTable />
        )}
      </Card>
    </Content>
  )
}

export default ContractualBehavior
